


import ColumnChart from "../charts/columnChart";
import DonutChart from "../charts/donutChart";
import { CHARTS_LABEL, TARGET_LABEL } from "../../constants/constants";
import { useMemo, useState,useEffect } from "react";
import useStore from "../../hooks/useStore";
import { observer } from "mobx-react";
import { Accordion } from "react-bootstrap";
import { Form } from "react-bootstrap";
import WasteAccordion from "./wasteAccordion";
import ShowPerformance from "../showPerformance";
import { toJS } from "mobx";
import { calculateMaxWithIncrease, getRoundedValue } from "../../utils/helpers";
import CustomSpinner from "../spinner/Spinner";

function DashboardWaste({ year }) {
  // Add state for selected unit
  const [selectedUnit, setSelectedUnit] = useState("MT");
  
  // Conversion logic
  const convertValue = (value, fromUnit, toUnit) => {
    if (fromUnit === toUnit) return value;
    if (fromUnit === "MT" && toUnit === "Kg") return value * 1000;
    if (fromUnit === "MT" && toUnit === "M3") return value * 1;
    if (fromUnit === "MT" && toUnit === "L") return value * 1000;
    return value;
  };
  
  const {
    dashboardStore: { dashboardDetails },
    companyStore: { permissibleLimit, companyData },
  } = useStore();
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  
  const columnChartData = useMemo(() => {
    const tempData = dashboardDetails?.charts?.waste;
    const fromUnit = "MT"; // Backend sends data in MT
    const toUnit = selectedUnit;
    
    const getGroupValue = (data, year, group, key = "prevValue") => {
      const merged = data?.[year]?.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      const value = merged?.[group]?.[key] || 0;
      return convertValue(value, fromUnit, toUnit);
    };
    
    return ["A", "D", "E"].map((group) => {
      const dataPoints = [year, year-1].map((yr) => getRoundedValue(getGroupValue(tempData, yr, group, "prevValue")));
      const targetPoints = [year, year-1].map((yr) => getRoundedValue(getGroupValue(tempData, yr, group, "target")));
      
      return {
        name: CHARTS_LABEL.waste[group],
        data: dataPoints,
        target: targetPoints,
        // ✅ Add units array for tooltip - each data point gets the selected unit
        units: dataPoints.map(() => selectedUnit),
      };
    });
  }, [dashboardDetails?.charts?.waste, year, selectedUnit]);

  const handleAccordionToggle = () => {
    setIsAccordionOpen(!isAccordionOpen);
  };

  const doughNutChartData = useMemo(() => {
    const tempData = dashboardDetails?.charts?.waste?.[year];
    const fromUnit = "MT";
    const toUnit = selectedUnit;
    
    const result = ["A", "D", "E"].map((group) => {
      const value = tempData?.reduce((acc, curr) => ({ ...acc, ...curr }), {})?.[group]?.prevValue || 0;
      return convertValue(value, fromUnit, toUnit);
    });
    
    const labels = ["A", "D", "E"].map((group) => CHARTS_LABEL.waste[group]);
    return { data: result || [], labels };
  }, [dashboardDetails?.charts?.waste, year, selectedUnit]);

  const assignedKpi = toJS(companyData)?.assignKpi?.find(
    (e) => e.kpiIdAssignKpi.name === "Waste"
  );

  const limits = permissibleLimit?.filter(
    ({ kpiId, subCatId, catId, year: tempYear }) =>
      kpiId === assignedKpi?.kpiId &&
      catId === assignedKpi?.catId &&
      subCatId === assignedKpi?.subCatId &&
      year == tempYear
  );

  const [maxLimitValue, setMaxLimitValue] = useState(0);
  useEffect(() => {
    let allValues = [
      ...(limits?.map((ele) => {
        // Convert permissible limit values to selected unit
        return convertValue(ele.value, "MT", selectedUnit);
      }) || []),
      ...["A", "D", "E"].flatMap((group) =>
        [year - 1, year].map((yr) => {
          const value = dashboardDetails?.charts?.waste?.[yr]?.reduce((acc, curr) => ({ ...acc, ...curr }), {})[group]?.prevValue || 0;
          return convertValue(value, "MT", selectedUnit);
        })
      ),
    ];
    setMaxLimitValue(Math.max(0, ...allValues) * 1.1);
  }, [dashboardDetails?.charts?.waste, limits, year, selectedUnit]);

  return (
    <div className="row h-100">
      <div className="col-xl-12">
        <div className="card h-100 dashboard-card ">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-start">
              <h5 className="mb-3 text-muted"> Waste Report</h5>
              <div className="ms-auto">
                <Form.Select
                  value={selectedUnit}
                  onChange={e => setSelectedUnit(e.target.value)}
                  className="w-auto px-3 py-2 border-primary rounded"
                  style={{ minWidth: "100px", fontWeight: "500" }}
                >
                  <option value="MT">MT</option>
                  <option value="Kg">Kg</option>
                  <option value="M3">M3</option>
                  <option value="L">L</option>
                </Form.Select>
              </div>
            </div>
            {dashboardDetails?.charts ? (
              <ColumnChart
                key={`waste-chart-${selectedUnit}`} // ✅ Force re-render when unit changes
                max={maxLimitValue}
                data={columnChartData}
                unit={selectedUnit} // ✅ Pass the selected unit to ColumnChart
                categories={[`CY${year}`, `CY${year-1}`]}
                yaxis={
                  limits?.map((ele) => ({
                    y: getRoundedValue(ele.value) || 0,
                    borderColor: "#FF4560",
                    label: {
                      borderColor: "#FF4560",
                      style: {
                        color: "#fff",
                        background: "#FF4560",
                      },
                      text: `${TARGET_LABEL.waste[ele.group]}: ${ele.value} ${ele.unitIdMaster.short_name}`,
                    },
                  })) || []
                }
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-75">
                <CustomSpinner />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="col-md-12 h-100 mt-4">
        {/* achievements and target  */}
        <div className="row">
          <div className="col-md-6 mb-4">
            <ShowPerformance label="waste" />
          </div>
          {/* donut chart  */}
          <div className="col-md-6 mb-4">
            <div className="card mb-0 h-100 dashboard-donut-card">
              <div className="card-body">
                <div className="d-flex flex-wrap align-items-start">
                  <h5 className="mb-3 text-muted donut-heading">
                    Waste Generated by Sources
                  </h5>
                  <div className="ms-auto">
                    <div className="toolbar d-flex flex-wrap gap-2 ">
                      <div className="">
                        <p className="text-muted mb-1">Unit</p>
                        <h6 className="unit-value">
                          {selectedUnit} {/* ✅ Show the selected unit */}
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
                {dashboardDetails?.charts ? (
                  <div className="donut">
                    <DonutChart
                      data={doughNutChartData.data || []}
                      labels={doughNutChartData.labels || []}
                      unit={selectedUnit} 
                    />
                  </div>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-75">
                    <CustomSpinner />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Accordion
        className="dashboard-chart-accordion"
        onSelect={handleAccordionToggle}
      >
        <Accordion.Item key={`waste-accordian`} eventKey={0}>
          <Accordion.Header>
            {isAccordionOpen ? "See less" : "See more"}
          </Accordion.Header>
          <Accordion.Body>
            <WasteAccordion year={year} selectedUnit={selectedUnit} />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
}

export default observer(DashboardWaste);